import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Loading } from "@/components/common";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Players from "@/pages/Players";
import PlayerProfile from "@/pages/PlayerProfile";
import NewMatch from "@/pages/NewMatch";
import LiveMatch from "@/pages/LiveMatch";
import HeadToHead from "@/pages/HeadToHead";
import Leaderboards from "@/pages/Leaderboards";
import MatchHistory from "@/pages/MatchHistory";
import Account from "@/pages/Account";
import Practice from "@/pages/Practice";
import PracticeLive from "@/pages/PracticeLive";
import GhostPlay from "@/pages/GhostPlay";
import Feed from "@/pages/Feed";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loading label="Starting up" />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function LoginRoute() {
  const { user, ready } = useAuth();
  if (!ready) return <Loading label="Starting up" />;
  if (user) return <Navigate to="/" replace />;
  return <Auth />;
}

function AuthCallback() {
  const { googleCallback } = useAuth();
  const navigate = useNavigate();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    (async () => {
      try {
        if (sid) await googleCallback(sid);
      } catch {}
      window.history.replaceState(null, "", "/");
      navigate("/", { replace: true });
    })();
  }, [googleCallback, navigate]);
  return <Loading label="Signing you in" />;
}

function Root() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/join/:code" element={<LoginRoute />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/players" element={<Protected><Players /></Protected>} />
      <Route path="/players/:id" element={<Protected><PlayerProfile /></Protected>} />
      <Route path="/new" element={<Protected><NewMatch /></Protected>} />
      <Route path="/match/:id" element={<Protected><LiveMatch /></Protected>} />
      <Route path="/head-to-head" element={<Protected><HeadToHead /></Protected>} />
      <Route path="/leaderboards" element={<Protected><Leaderboards /></Protected>} />
      <Route path="/history" element={<Protected><MatchHistory /></Protected>} />
      <Route path="/club" element={<Protected><Account /></Protected>} />
      <Route path="/feed" element={<Protected><Feed /></Protected>} />
      <Route path="/practice" element={<Protected><Practice /></Protected>} />
      <Route path="/practice/:id" element={<Protected><PracticeLive /></Protected>} />
      <Route path="/ghost" element={<Protected><GhostPlay /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" theme="dark" richColors />
          <Root />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
